import { createGameCollectionClock } from './107-game-collection-clock.mjs';
import { openCompetition } from './104-game-competition.mjs';
import {
  createGameChrome,
  createGameShell,
  focusGameControl,
  playbackFeedback,
  gameRoundStatus,
  createBoardPlayback,
} from './81-game-card-chrome.mjs';
import { createTimedRunPresentation } from './80-timed-round.mjs';
import {
  createTimedSeries,
  applyTimedSeries,
  TIMED_SERIES_VERSION,
} from '../../../packages/domain/src/69-timed-series.mjs';
import { shareGame } from './66-game-sharing.mjs';
import { paintGameOptions, createGameTools } from './55-game-options.mjs';
import { renderArcadePlayer, createGameAction } from './70-arcade-players.mjs';
import { captureHanoi, animateHanoi, cancelHanoi } from './67-progressive-puzzles.mjs';
import { availableGames, getGame } from '../../../packages/domain/src/49-game-catalog.mjs';
import {
  createGame,
  applyGameMove,
  gameView,
  GAME_ENGINE_VERSION,
} from '../../../packages/domain/src/50-game-engine.mjs';
import {
  paintGameBoard,
  capturePour,
  cancelPour,
  animatePour,
  paintGameScene,
} from './35-game-board.mjs';
import { createBoardDecorations } from './185-board-decorations.mjs';
import { attachGameInput } from './37-game-input.mjs';
import { createGemHint } from './39-gem-playback.mjs';
import { presentGameReward } from './42-game-rewards.mjs';
import { attachBlockDrag } from './44-block-playback.mjs';
import { createGameImmersion } from './46-game-immersion.mjs';
import { createGameProgression } from './47-game-progression.mjs';
import { attachGemSparkle } from './48-game-sparkle.mjs';
import { feedGameAt } from '../../../packages/domain/src/54-feed-play.mjs';

const seedValue = () => crypto.getRandomValues(new Uint32Array(1))[0];

/** One-screen, scroll-first games. The DOM owns transient private rounds; only an
 * explicit score preference permits server sessions and submitted move transcripts.
 * No game action is a reading acknowledgement or a preference-training signal. */
export function createGameCards(ctx) {
  const players = new Set();
  const clock = createGameCollectionClock(players);
  const syncClock = clock.sync;
  const eligibleGames = () => availableGames(ctx.state?.features);
  function at(ordinal) {
    return feedGameAt(
      ordinal,
      {
        mode: ctx.mode,
        scope: ctx.feedScope,
        contentScope: ctx.contentScope ?? 'ordinary',
        format: ctx.experience,
        enabled: ctx.state?.features?.games === true,
        gamesFeed: ctx.state?.settings?.gamesFeed,
      },
      eligibleGames(),
    );
  }
  function render(game, { seed = seedValue(), standalone = false, challenge = null } = {}) {
    game = getGame(game?.id);
    if (!game?.available) throw new TypeError('Choose an available game.');
    const custom = renderArcadePlayer(ctx, game, { seed, standalone, players });
    if (custom) return custom;
    const action = (text, label, fn, icon) => createGameAction(ctx, text, label, fn, icon);
    const actor = ctx.state?.me?.id;
    const { host, heading } = createGameShell(ctx, game, action, () =>
      shareGame(ctx, game, seed, GAME_ENGINE_VERSION),
    );
    const board = ctx.e('div', 'game-board');
    const immersion = createGameImmersion(ctx, host, board, { standalone });
    heading.append(immersion.button);
    board.setAttribute('role', 'group');
    board.setAttribute('aria-label', game.title + ' playing surface');
    if (game.id === 'soft-merge') board.tabIndex = 0;
    const {
      playfield,
      controls,
      scene,
      options,
      feedback,
      arcade,
      roundLabel,
      runBest,
      celebration,
      status,
      fill,
      tally,
      play,
      undo,
      more,
      moreActions,
      paintArcade,
      paintRoundControls,
      guide,
    } = createGameChrome(ctx, {
      game,
      board,
      host,
      heading,
      action,
      start,
      togglePause,
      resetRound,
      undoMove,
      finishRound,
      showScores,
      paint,
    });
    let state = createGame(game.id, seed),
      history = [],
      moves = [],
      activeMs = 0,
      lastTick = 0,
      started = game.kind === 'idle',
      paused = false,
      done = false,
      practice = game.kind === 'score',
      intersecting = standalone,
      session = null,
      pendingSubmission = null,
      busy = false,
      disposed = false,
      resolving = false,
      dismissMs = 0;
    const timed = createTimedRunPresentation(ctx, arcade, {
      onExpire: finishRound,
      onContinue: () => move({ type: 'continue' }),
      onTimer: (ms) => {
        activeMs = ms;
        tally.textContent = `${state.score} points`;
        fill.style.transform = `scaleX(${1 - ms / 120000})`;
      },
    });
    const visible = () =>
      host.isConnected && (standalone || host.dataset.immersive === 'true' || intersecting);
    let best = 0;
    let rewardAnimations = [];
    const optionNodes = new Map();
    const { tools, toolsSummary, toolActions, detachDisclosures, pictureStudio } = createGameTools(
      ctx,
      game,
      {
        more,
        moreActions,
        host,
        canOpen: () =>
          !busy && !resolving && !disposed && allowed() && (!started || practice || done),
        onOpen: haltEffects,
      },
    );
    const progression = createGameProgression(
      ctx,
      host,
      (action) => {
        if (busy || resolving || paused || disposed || !allowed()) return false;
        if (action) {
          const previous = state;
          move(action);
          if (state === previous) return false;
        } else if (!practice && game.kind === 'score') {
          void start(true);
          return false;
        } else resetRound();
        focusGameControl(game.id, board, options, tools, toolsSummary);
        return true;
      },
      { before: options, anchor: playfield, onChapterChange: paint },
    );
    const detachInput = attachGameInput(board, () => state, move);
    const playbackOptions = {
      visible,
      onClear: (event) => {
        if (!disposed && allowed()) feedback.textContent = playbackFeedback(event);
      },
    };
    const playback = createBoardPlayback(ctx, board, playfield, game.id, playbackOptions);
    const blockDrag =
      game.id === 'block-fit' ? attachBlockDrag(ctx, board, options, () => state, move) : null;
    const gemHint =
      game.id === 'gem-swap'
        ? createGemHint(
            action,
            playback,
            feedback,
            () => state,
            () => !busy && !resolving && !paused && !done && allowed(),
          )
        : null;
    if (gemHint) controls.insertBefore(gemHint, more);
    const sparkle = attachGemSparkle(board, {
      visible: () => visible() && !resolving,
      readState: () => state,
    });
    const { creativeFeedback, marbles, bubbles, puzzles, craft } = createBoardDecorations(ctx, {
      game,
      host,
      board,
      scene,
      playfield,
      controls,
      arcade,
      roundLabel,
      runBest,
      feedback,
      undo,
      more,
      action,
      move,
      readState: () => state,
      allowed: () => !disposed && !busy && allowed(),
      visible,
      canInspect: () => !disposed && !busy && !resolving && !paused && !done && allowed(),
      restore: (snapshot) => {
        progression.reset();
        haltEffects();
        state = snapshot;
        history = [];
        status.textContent = '';
        paint();
      },
    });
    function haltEffects() {
      guide?.cancel();
      detachInput.cancel?.();
      creativeFeedback.cancel();
      playback.cancel();
      cancelPour(board);
      cancelHanoi(board);
      marbles?.cancel();
      bubbles?.cancel();
      blockDrag?.cancel();
      sparkle.update();
    }
    const player = {
      host,
      dispose,
      tick,
      needsClock: () => !disposed && allowed() && visible() && started && !paused && !done,
      pause: () => {
        if (game.kind === 'score' && started && !done) {
          paused = true;
          timed.suspend();
          paint();
        }
      },
    };
    players.add(player);
    const observer = new IntersectionObserver(
      ([entry]) => {
        intersecting = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        if (!visible()) haltEffects();
        sparkle.update();
        lastTick = 0;
        timed.suspend();
        syncClock();
      },
      { threshold: [0, 0.25, 0.5] },
    );
    observer.observe(host);
    const visibility = () => {
      lastTick = 0;
      timed.suspend();
      if (document.hidden) haltEffects();
      sparkle.update();
      syncClock();
    };
    document.addEventListener('visibilitychange', visibility);
    function currentActor() {
      return (ctx.state?.me?.id ?? null) === (actor ?? null);
    }
    function allowed() {
      return (
        currentActor() &&
        ctx.state?.features?.games === true &&
        ctx.state.features[`game-${game.id}`] === true
      );
    }
    function paint(renderBoard = true) {
      syncClock();
      progression.tick(
        performance.now(),
        visible() && !document.hidden && !paused && !busy && !resolving && allowed(),
      );
      const view = paintGameBoard(ctx, board, state, move, {
        render: renderBoard,
        disabled: paused || done || busy || resolving || !allowed(),
        motion: visible() && !document.hidden && !paused && !resolving,
      });
      paintGameScene(ctx, scene, state);
      sparkle.update();
      paintGameOptions(ctx, {
        view,
        game,
        options,
        tools,
        toolActions,
        optionNodes,
        move,
        action,
        disabled: paused || done || busy || resolving || !allowed(),
        readView: () => gameView(state),
      });
      best = Math.max(best, state.score);
      paintRoundControls({
        state,
        view,
        world: progression.world(view.stage),
        best,
        started,
        practice,
        paused,
        done,
        busy,
        resolving,
        allowed: allowed(),
        pendingSubmission,
        activeMs,
        historyLength: history.length,
        gemHint,
      });
      if (!status.textContent || status.dataset.engine === 'true') {
        status.textContent = view.status || '';
        status.dataset.engine = 'true';
      }
      marbles?.update(view);
      bubbles?.update(view);
      puzzles?.update(view);
      timed.paint(game.kind === 'score' && started && !practice);
      paintArcade(state, view, best);
      craft?.update(view);
      fill.style.transform = `scaleX(${Math.max(0, 1 - activeMs / 120000)})`;
    }
    async function start(
      restoreFocus = false,
      period = challenge?.period ?? 'all',
      challengeSeed = challenge?.seed ?? null,
    ) {
      if (!allowed() || busy || resolving || disposed) return;
      busy = true;
      status.dataset.engine = 'false';
      status.textContent = 'Getting your round ready…';
      paint();
      try {
        let nextSession = null;
        let nextSeed = challengeSeed ?? seedValue();
        if (actor && ctx.state.settings.recordScores) {
          const result = await ctx.api('/api/games/start', { gameId: game.id, period });
          if (disposed || !allowed() || !ctx.state.settings.recordScores) return;
          if (result.engineVersion !== TIMED_SERIES_VERSION || result.gameId !== game.id)
            throw new Error('This challenge has changed. Refresh before playing a recorded round.');
          nextSession = result;
          nextSeed = result.seed;
        }
        const candidate = createTimedSeries(game.id, nextSeed);
        session = nextSession;
        progression.reset();
        haltEffects();
        state = candidate;
        feedback.textContent = '';
        stopRewards();
        seed = nextSeed;
        history = [];
        moves = [];
        activeMs = 0;
        timed.reset(performance.now());
        dismissMs = 0;
        pendingSubmission = null;
        done = false;
        practice = false;
        started = true;
        paused = false;
        lastTick = 0;
        status.textContent = gameRoundStatus(session, ctx.state.settings.anonymousScores);
        for (const other of players) if (other !== player) other.pause();
      } catch (error) {
        status.textContent = error.message;
      } finally {
        busy = false;
        if (!disposed) paint();
        if (restoreFocus === true && !done && !disposed)
          focusGameControl(game.id, board, options, tools, toolsSummary);
      }
    }
    function move(input) {
      if (guide?.isOpen?.()) return;
      if (
        !practice &&
        started &&
        !paused &&
        allowed() &&
        timed.tick(performance.now(), {
          active: visible() && !document.hidden && !done && !busy,
          betweenBoards: state.status === 'between-boards',
          ready: !resolving,
          capped: gameView(state).moveLimitReached,
        })
      )
        return;
      if (!allowed() || busy || resolving || paused || done || disposed || craft?.isOpen()) return;
      if (!started) {
        started = true;
        practice = game.kind === 'score';
        lastTick = 0;
      }
      try {
        const previous = state;
        const outgoing = capturePour(board, previous);
        const outgoingTower = captureHanoi(board, previous);
        if (input.type === 'continue') progression.reset();
        state =
          practice || game.kind === 'idle'
            ? applyGameMove(state, input)
            : applyTimedSeries(state, input);
        if (game.kind === 'idle') {
          if (game.id === 'ball-sort' && state.data.stage !== previous.data.stage) history = [];
          if (game.id !== 'ball-sort' || state.data.transfers > previous.data.transfers)
            history.push(
              game.id === 'ball-sort'
                ? { ...previous, data: { ...previous.data, selected: -1 } }
                : previous,
            );
          if (history.length > (game.id === 'ball-sort' ? 600 : 50)) history.shift();
        } else moves.push(input);
        dismissMs = 0;
        const earned = state.score - previous.score;
        const view = gameView(state);
        feedback.textContent =
          earned > 0
            ? `+${earned}${view.combo > 1 ? ` · ${view.combo} chain` : ''}${view.chain > 1 ? ` · ${view.chain} cascades` : ''}`
            : '';
        status.dataset.engine = 'true';
        status.textContent = '';
        paint(!['gem-swap', 'cluster-pop'].includes(game.id));
        const pour = animatePour(board, previous, state, outgoing);
        marbles?.accepted(previous, state, pour);
        const towerFlight = animateHanoi(board, previous, state, outgoingTower);
        creativeFeedback.play(previous, state, towerFlight);
        const accepted = state,
          message = feedback.textContent;
        const focusedCell =
          document.activeElement?.parentElement === board ? document.activeElement : null;
        const sequence = playback.play(previous, accepted, view.events || []);
        if (!sequence && ['gem-swap', 'cluster-pop'].includes(game.id)) paint();
        if (sequence) {
          resolving = true;
          bubbles?.update(view);
          sparkle.update();
          play.disabled = true;
          void sequence.finally(() => {
            if (disposed || state !== accepted || !currentActor()) return;
            resolving = false;
            if (!allowed() || done) return paint();
            feedback.textContent = message;
            paint();
            if (document.activeElement === document.body)
              focusedCell?.focus?.({ preventScroll: true });
            celebrate(previous, view, earned);
            if (practice && (state.completed || moves.length >= 600)) void finishRound();
          });
          return;
        }
        celebrate(previous, view, earned);
        if (practice && game.kind === 'score' && (state.completed || moves.length >= 600))
          void finishRound();
      } catch (error) {
        status.dataset.engine = 'false';
        status.textContent = error.message;
      }
    }
    function stopRewards() {
      for (const animation of rewardAnimations) animation.cancel();
      rewardAnimations = [];
      celebration.hidden = true;
    }
    function celebrate(previous, view, earned) {
      if (state.completed || view.moveLimitReached || view.stageComplete)
        progression.show(state, {
          privatePlay: !session,
          timedBoard: !practice && game.kind === 'score',
          creation: game.tags.includes('creative'),
          puzzle: game.kind === 'idle',
          advanceAction: view.advanceAction,
          advanceLabel: view.advanceLabel,
          completionTitle: view.completionTitle,
          stage: view.stage,
          reason: view.completionReason,
        });
      if (game.kind !== 'score' || !visible() || document.hidden || paused) return;
      stopRewards();
      presentGameReward(
        ctx,
        { game, state, previous, view, earned, board, feedback, celebration },
        (animation) => rewardAnimations.push(animation),
      );
    }
    function togglePause() {
      haltEffects();
      paused = !paused;
      timed.suspend();
      lastTick = 0;
      paint();
    }
    function undoMove() {
      if (history.length) {
        progression.reset();
        haltEffects();
        state = history.pop();
        status.textContent = '';
        paint();
      }
    }
    function resetRound() {
      if (busy) return;
      progression.reset();
      seed = seedValue();
      haltEffects();
      resolving = false;
      state = createGame(game.id, seed);
      history = [];
      moves = [];
      session = null;
      pendingSubmission = null;
      practice = game.kind === 'score';
      dismissMs = 0;
      feedback.textContent = '';
      stopRewards();
      activeMs = 0;
      timed.reset();
      lastTick = 0;
      started = game.kind === 'idle';
      paused = false;
      done = false;
      status.textContent = '';
      paint();
    }
    async function finishRound() {
      if (!started || (done && !pendingSubmission) || busy || disposed) return;
      done = true;
      paused = false;
      if (!practice) {
        haltEffects();
        stopRewards();
      }
      progression.show(state, {
        privatePlay: false,
        timed: true,
        completionTitle: !practice && activeMs >= 120000 ? 'Time up!' : null,
      });
      lastTick = 0;
      status.dataset.engine = 'false';
      status.textContent = `${state.score} points · Private round, not recorded`;
      if (
        session &&
        activeMs === 120000 &&
        moves.length &&
        allowed() &&
        ctx.state.settings.recordScores
      ) {
        pendingSubmission ??= {
          sessionId: session.sessionId,
          moves: [...moves],
          activeMs: Math.max(1, Math.ceil(activeMs)),
        };
        busy = true;
        try {
          const result = await ctx.api('/api/games/finish', pendingSubmission);
          pendingSubmission = null;
          if (!disposed && allowed())
            status.textContent = `${result.score} points · Score recorded`;
        } catch (error) {
          if (!disposed) status.textContent = `${state.score} local points · ${error.message}`;
        } finally {
          busy = false;
        }
      } else pendingSubmission = null;
      if (!disposed) paint();
    }
    function showScores() {
      if (!allowed() || busy) return;
      if (started && !done) {
        paused = true;
        timed.suspend();
      }
      lastTick = 0;
      paint();
      openCompetition(ctx, game, {
        allowed: () => !disposed && allowed(),
        start: (period, challengeSeed) => start(true, period, challengeSeed),
      });
    }
    function tick() {
      if (disposed) return;
      if (!host.isConnected || !currentActor()) {
        dispose();
        return;
      }
      if (pictureStudio?.refresh()) {
        lastTick = 0;
        return;
      }
      const now = performance.now();
      progression.tick(
        now,
        visible() && !document.hidden && !paused && !busy && !resolving && allowed(),
      );
      const active =
        started && !paused && !done && !busy && visible() && !document.hidden && allowed();
      bubbles?.tick(now, active && !resolving);
      const elapsed = active && lastTick ? Math.max(0, now - lastTick) : 0;
      lastTick = active ? now : 0;
      timed.tick(now, {
        active: active && game.kind === 'score' && !practice,
        betweenBoards: state.status === 'between-boards',
        ready: !resolving,
        capped: gameView(state).moveLimitReached,
      });
      if (active && !done) {
        const dismissal = gameView(state).autoDismiss;
        if (dismissal && !resolving) {
          dismissMs += elapsed;
          if (dismissMs >= dismissal.delayMs) move(dismissal.move);
        } else dismissMs = 0;
      }
    }
    function dispose() {
      guide?.dispose();
      immersion.dispose();
      progression.dispose();
      timed.dispose();
      creativeFeedback.dispose();
      pictureStudio?.dispose();
      sparkle.dispose();
      disposed = true;
      haltEffects();
      playback.dispose?.();
      blockDrag?.dispose();
      stopRewards();
      observer.disconnect();
      detachInput();
      marbles?.dispose();
      bubbles?.dispose();
      optionNodes.clear();
      detachDisclosures();
      document.removeEventListener('visibilitychange', visibility);
      players.delete(player);
      history = [];
      moves = [];
      session = null;
      pendingSubmission = null;
      if (!players.size) clock.dispose();
    }
    host.disposeGame = dispose;
    host.refreshGame = paint;
    paint();
    // Register after attachment. A removed card releases all private round data.
    queueMicrotask(syncClock);
    return host;
  }
  return {
    at,
    render,
    dispose: () => {
      for (const player of [...players]) player.dispose();
    },
  };
}

export { playbackFeedback } from './81-game-card-chrome.mjs';
